import styled from '@emotion/styled';
import {
  Bar1D,
  BarSingle1D,
  Model,
  Pie1D,
  TableResult,
  TextResult,
} from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import Markdown from '../../ui/Markdown';
import { Bottom, PageLayout as PageLayoutBase, Title } from '../layout';
import { PdfLegendDispatch, PdfResultDispatch } from '../PdfResultsDispatch';
import { PaginatedResult } from '../utils';
import { Footer } from './Footer';

import backgroundImage from './bgimage.jpg';

const backgroundStyles = {
  topRight: `
    background-image: linear-gradient(to right top, rgba(255,255,255,1), rgba(255,255,255,0)),
                      url(${backgroundImage});
    background-repeat: no-repeat;
    background-position: top -20px right -180px;
    background-size: 500px auto;
  `,
  bottomLeft: `
    background-image: linear-gradient(to left bottom, rgba(255,255,255,1), rgba(255,255,255,0)),
                      url(${backgroundImage});
    background-repeat: no-repeat;
    background-position: bottom -30px left -120px;
    background-size: 400px auto;
  `,
};

export type PageLayoutProps = {
  backgroundColor: string;
  backgroundVariant?: 'topRight' | 'bottomLeft'; // replaces multiple props
  withBackgroundImage?: boolean;
};

export const PageLayout = styled(PageLayoutBase)<PageLayoutProps>`
  background-color: ${(props) => props.backgroundColor};
  ${(props) =>
    props.withBackgroundImage && props.backgroundVariant
      ? backgroundStyles[props.backgroundVariant]
      : ''}
  padding: 48px;
  padding-bottom: calc(2rem + 80px);
  box-sizing: border-box;
  position: relative;
`;

export type MainProps = {
  noPadding?: boolean;
  compactLayout?: boolean;
};

const Main = styled.div`
  background-color: white;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: ${(props: MainProps) =>
    props.compactLayout ? '' : 'space-between'};
  padding: ${(props: MainProps) => props.noPadding ?? '36px'};
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  color: var(--darkblue);
`;

const GraphContainter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Graph = styled.div`
  width: 100%;
  height: 400px;
`;

const Legend = styled.div`
  width: 100%;
`;

export type ResultFullPageType =
  | Pie1D
  | Bar1D
  | BarSingle1D
  | TableResult
  | TextResult;

const ResultFullPage = ({
  paginatedResult,
  model,
  year,
}: {
  paginatedResult: PaginatedResult;
  model?: Model;
  year: number | undefined;
}) => {
  const result = paginatedResult.results
    ? (paginatedResult.results[0] as ResultFullPageType)
    : null;

  if (!result) return null;

  if (result?.type === 'table' || result?.type === 'text') {
    return (
      <PageLayout backgroundColor="white">
        <Main noPadding compactLayout>
          <ResultFullPageContent result={result} model={model} />
        </Main>
        <Bottom>
          <Footer
            year={year}
            pageNumber={paginatedResult.pageNumber}
            backgroundType="light"
          />
        </Bottom>
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundColor="var(--darkblue)">
      <Main>
        <ResultFullPageContent result={result} model={model} />
      </Main>
      <Bottom>
        <Footer year={year} pageNumber={paginatedResult.pageNumber} />
      </Bottom>
    </PageLayout>
  );
};

const ResultFullPageContent = ({
  result,
  model,
}: {
  result: ResultFullPageType;
  model?: Model;
}) => {
  const { t } = useTranslation(['model']);
  if (!model) return null;
  const translationPrefix = `model:${model?.name.toLowerCase()}.result.${result.code?.toLowerCase()}`;
  const isDescription = result?.type !== 'text';
  const description = isDescription
    ? t(`${translationPrefix}.description`)
    : null;
  const isDescriptionMarkdown = result.type === 'table';
  return (
    <>
      <Header>
        <Title>
          {t(
            getResultFullPageTitleTranslationPrefix(result, model.name),
          ).toLocaleUpperCase()}
        </Title>
        {description &&
          (isDescriptionMarkdown ? (
            <Markdown>{description}</Markdown>
          ) : (
            <Description>{description}</Description>
          ))}
      </Header>

      {result?.type === 'table' || result?.type === 'text' ? (
        <GraphContainter>
          <PdfResultDispatch result={result} model={model} />
        </GraphContainter>
      ) : (
        <GraphContainter>
          <Graph>
            <PdfResultDispatch result={result} model={model} />
          </Graph>
          <Legend>
            <PdfLegendDispatch result={result} model={model} />
          </Legend>
        </GraphContainter>
      )}
    </>
  );
};

export const getResultFullPageTitleTranslationPrefix = (
  result: ResultFullPageType,
  modelName: string,
) => {
  return `model:${modelName.toLowerCase()}.result.${result.code?.toLowerCase()}.title`;
};

export default ResultFullPage;
